<script lang="ts">
  export interface CollapsibleGroupProps {
    class?: HTMLAttributes['class']
    contentWrapperClass?: HTMLAttributes['class']
    id?: string
    /** The title of the collapsible group. */
    title: string
    /** The icon of the collapsible group. */
    icon?: IconValues
    /** The description of the collapsible group. */
    description?: string
    /** Whether the collapsible group is open. @default false */
    open?: boolean
    /** Whether the collapsible group is open by default. @default false */
    defaultOpen?: boolean
    /** Whether the collapsible group is collapsible. @default true */
    isCollapsible?: boolean
  }
</script>

<script setup lang="ts">
  import { GatIcon } from '../gat-icon'
  import { onMounted, reactive, ref, watch, type HTMLAttributes } from 'vue'
  import { cn } from '../../lib/utils'
  import { type IconValues } from '@gat-ship/icons'
  import { globalUtils } from '@gat-ship/utils'

  const props = withDefaults(defineProps<CollapsibleGroupProps>(), {
    defaultOpen: false,
    isCollapsible: true
  })

  const emit = defineEmits<{
    (e: 'update:open', value: boolean): void
  }>()

  const hasEventsBound = reactive({
    updateOpen: false
  })
  onMounted(() => {
    hasEventsBound.updateOpen =
      globalUtils.functions.components.hasEmitListener('update:open')
  })

  const _open = ref(props.open ?? props.defaultOpen)

  function toggle() {
    _open.value = !_open.value
    if (hasEventsBound.updateOpen && props.isCollapsible) {
      emit('update:open', _open.value)
    }
  }

  watch(
    () => props.open,
    (newValue) => {
      _open.value = newValue ?? props.defaultOpen
    },
    {
      immediate: true
    }
  )

  // Transition functions
  function onEnter(el: Element) {
    const target = el as HTMLElement
    target.style.height = '0'
    target.style.opacity = '0'

    // Force reflow
    target.offsetHeight

    target.style.transition = 'height 100ms ease-out, opacity 100ms ease-out'
    target.style.height = target.scrollHeight + 'px'
    target.style.opacity = '1'
  }

  function onLeave(el: Element) {
    const target = el as HTMLElement
    target.style.transition = 'height 100ms ease-in, opacity 100ms ease-in'
    target.style.height = '0'
    target.style.opacity = '0'
  }

  function onAfterEnter(el: Element) {
    const target = el as HTMLElement
    target.style.height = ''
    target.style.transition = ''
  }

  function onAfterLeave(el: Element) {
    const target = el as HTMLElement
    target.style.height = ''
    target.style.transition = ''
  }
</script>

<template>
  <div
    :id="id"
    :class="
      cn(
        'bg-background focus-visible:ring-primary flex flex-col rounded-sm border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        props.class
      )
    "
    @click="toggle"
    @keydown.enter.stop="toggle"
    @keydown.space.prevent.stop="toggle"
    tabindex="0"
    role="button"
    :aria-expanded="_open"
    :aria-label="`${_open ? 'Collapse' : 'Expand'} ${title || 'section'}`"
  >
    <slot name="trigger" v-bind="{ open: _open }">
      <div
        class="hover:bg-accent flex min-h-16 shrink-0 grow items-center gap-2 p-4 hover:cursor-pointer"
        :class="{ 'border-b': _open }"
      >
        <div class="flex grow flex-row items-center gap-2">
          <gat-icon
            v-if="icon"
            :icon="icon"
            class="size-6"
            :stroke-width="1.5"
          />
          <div class="flex grow flex-col gap-1">
            <div class="text-foreground text-sm font-medium leading-none">
              {{ title }}
            </div>
            <div class="text-soft text-xs leading-none" v-if="description">
              {{ description }}
            </div>
          </div>
        </div>
        <slot name="actions" />
        <gat-icon
          :icon="'ChevronDown'"
          class="size-4 transition-all duration-150"
          :class="{ 'rotate-180': _open }"
        />
      </div>
    </slot>
    <transition
      name="expand-collapse"
      mode="out-in"
      @enter="onEnter"
      @leave="onLeave"
      @after-enter="onAfterEnter"
      @after-leave="onAfterLeave"
    >
      <div
        v-show="_open"
        :class="
          cn(
            'content-wrapper shadow-xs flex flex-col overflow-hidden rounded-md dark:shadow-neutral-950',
            props.contentWrapperClass
          )
        "
        @click.stop
      >
        <slot name="content" />
      </div>
    </transition>
  </div>
</template>
